import React, { useEffect, useState } from 'react';
import { Download, Share, X } from 'lucide-react';
import {
  INAPP_HINT_KEY,
  INSTALL_HINT_KEY,
  dismissHint,
  forcedHint,
  getIosBrowserName,
  getIosInstallCopy,
  isHintDismissed,
  isInAppBrowser,
  isIosInstallable,
  isStandaloneApp,
} from './environment';

const DismissButton = ({ onClick, testId }) => (
  <button
    onClick={onClick}
    aria-label="Dismiss"
    data-testid={testId}
    className="shrink-0 text-mono-mute hover:text-white transition-colors p-1 -m-1"
  >
    <X className="h-4 w-4" />
  </button>
);

export function InAppBrowserNotice() {
  const [show, setShow] = useState(
    () => (forcedHint() === 'inapp' || isInAppBrowser()) && !isHintDismissed(INAPP_HINT_KEY),
  );
  if (!show) return null;

  return (
    <div className="bg-mono-card px-4 py-3 flex items-start gap-4" data-testid="free-inapp-notice">
      <p className="flex-1 text-mono-mute font-body text-sm leading-relaxed">
        You’re in an in-app browser. Open app.coachrands.com in Safari to add this to your home screen.
      </p>
      <DismissButton
        testId="free-inapp-notice-dismiss"
        onClick={() => {
          dismissHint(INAPP_HINT_KEY);
          setShow(false);
        }}
      />
    </div>
  );
}

export function InstallPromptBar({ sessionCount }) {
  const [nativePrompt, setNativePrompt] = useState(null);
  const [dismissed, setDismissed] = useState(() => isHintDismissed(INSTALL_HINT_KEY));

  useEffect(() => {
    const capture = (event) => {
      event.preventDefault();
      setNativePrompt(event);
    };
    window.addEventListener('beforeinstallprompt', capture);
    return () => window.removeEventListener('beforeinstallprompt', capture);
  }, []);

  if (dismissed) return null;

  const forced = forcedHint();
  const forcedIos = forced === 'ios';
  const forcedAndroid = forced === 'android';
  if (!forcedIos && !forcedAndroid) {
    if (isInAppBrowser() || isStandaloneApp() || !sessionCount) return null;
    if (!isIosInstallable() && !nativePrompt) return null;
  }

  const iosStyle = forcedIos || (!forcedAndroid && isIosInstallable());
  const Icon = iosStyle ? Share : Download;
  const iosCopy = getIosInstallCopy(forcedIos ? null : getIosBrowserName());

  const close = () => {
    dismissHint(INSTALL_HINT_KEY);
    setDismissed(true);
  };

  const install = async () => {
    if (!nativePrompt) return;
    nativePrompt.prompt();
    await nativePrompt.userChoice;
    close();
  };

  return (
    <div
      className="bg-mono-card border-l-2 border-crimson px-4 py-3 flex items-center gap-4"
      data-testid="free-install-bar"
    >
      <Icon className="h-4 w-4 text-crimson shrink-0" />
      {iosStyle ? (
        <p className="flex-1 text-mono-mute font-body text-sm leading-relaxed" data-testid="free-install-ios-text">
          {iosCopy}
        </p>
      ) : (
        <>
          <p className="flex-1 text-mono-mute font-body text-sm leading-relaxed" data-testid="free-install-android-text">
            Add Coach Rands to your home screen for one-tap access.
          </p>
          <button
            onClick={install}
            data-testid="free-install-native-btn"
            className="shrink-0 bg-crimson hover:bg-crimson-bright text-white font-heading text-xs font-bold uppercase tracking-[0.15em] px-4 py-2 transition-colors"
          >
            Install
          </button>
        </>
      )}
      <DismissButton testId="free-install-bar-dismiss" onClick={close} />
    </div>
  );
}
